import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 280, damping: 22 } },
};

/* Simple waveform bars for playing state */
function WaveformBars() {
  return (
    <div className="music-wave">
      {[1, 2, 3, 4].map((i) => (
        <span key={i} className="music-wave__bar" style={{ animationDelay: `${i * 0.12}s` }} />
      ))}
    </div>
  );
}

function Musicas({ video, setVideo, onUpdate, setVisivel, user, creditos }) {
  const [tab, setTab] = useState("sistema");
  const [musicas, setMusicas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tocandoId, setTocandoId] = useState(null);
  const [aplicandoId, setAplicandoId] = useState(null);
  const [successId, setSuccessId] = useState(null);
  const audioRef = useRef(new Audio());

  // Minha Biblioteca
  const [minhasMusicas, setMinhasMusicas] = useState([]);
  const [loadingMinhas, setLoadingMinhas] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [tooltipVisible, setTooltipVisible] = useState({});

  const musicaAtualId = video?.musica_id || null;
  const musicaAtualCustomUrl = video?.musica_custom_url || null;

  useEffect(() => {
    setLoading(true);
    fetch("https://videooferta.com/api/listar_musicas.php")
      .then((res) => res.json())
      .then((data) => setMusicas(data))
      .catch((err) => console.error("Erro ao buscar musicas:", err))
      .finally(() => setLoading(false));

    return () => {
      audioRef.current.pause();
      audioRef.current.src = "";
    };
  }, []);

  // Carrega musicas da biblioteca do usuario
  useEffect(() => {
    if (!user?.id) return;
    setLoadingMinhas(true);
    fetch(`https://videooferta.com/api/listar_musicas_usuario.php?id_user=${user.id}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.musicas) setMinhasMusicas(data.musicas);
      })
      .catch((err) => console.error("Erro ao buscar musicas do usuario:", err))
      .finally(() => setLoadingMinhas(false));
  }, [user?.id, tab]);

  function togglePlay(musica) {
    const audio = audioRef.current;
    if (tocandoId === musica.id) {
      audio.pause();
      setTocandoId(null);
      return;
    }
    audio.pause();
    audio.currentTime = 0;
    audio.src = musica.url;
    audio.play();
    setTocandoId(musica.id);
    audio.onended = () => setTocandoId(null);
  }

  async function handleClickMusica(musicaId, musicaUrl, personalizada) {
    setAplicandoId(musicaId);
    try {
      const formData = new FormData();
      formData.append("video_id", video.video_id);
      formData.append("musica_id", personalizada ? 0 : musicaId);
      if (personalizada && musicaUrl) formData.append("musica_custom_url", musicaUrl);
      const res = await fetch("https://videooferta.com/api/atualizar_musica_video.php", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        // Atualizar apenas campos de música sem recarregar todo o vídeo
        setVideo(prev => ({
          ...prev,
          musica_id: personalizada ? 0 : musicaId,
          musica_url: musicaUrl || prev.musica_url,
          musica_custom_url: personalizada ? musicaUrl : null,
        }));
        setSuccessId(musicaId);
        setTimeout(() => setSuccessId(null), 2000);
      } else {
        alert("Erro ao atualizar musica: " + data.error);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setAplicandoId(null);
    }
  }

  // Upload de musica
  async function handleUpload(file) {
    if (!file) return;
    if (!user?.id) {
      alert("Voce precisa estar logado para enviar musicas.");
      return;
    }

    // Validar tipo
    if (!file.type.startsWith("audio/")) {
      alert("Por favor, envie apenas arquivos de audio (MP3, WAV, etc).");
      return;
    }

    // Limitar tamanho (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert("Arquivo muito grande. Maximo 10MB.");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append("audio", file);
      formData.append("id_user", user.id);

      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", "https://videooferta.com/api/upload_musica_usuario.php");
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error("Resposta invalida")); }
        };
        xhr.onerror = () => reject(new Error("Erro de rede"));
        xhr.send(formData);
      });
      if (data.success) {
        // Adiciona na lista local
        const novaMusica = data.musica || {
          id: data.id,
          titulo: data.nome,
          url: data.url,
          personalizada: true,
          zero_creditos: Number(creditos) <= 0,
        };
        setMinhasMusicas((prev) => [novaMusica, ...prev]);

        // Mostra tooltip se usuario tem 0 creditos
        if (Number(creditos) <= 0) {
          setTooltipVisible((prev) => ({ ...prev, [novaMusica.id]: true }));
          setTimeout(() => {
            setTooltipVisible((prev) => ({ ...prev, [novaMusica.id]: false }));
          }, 10000);
        }
      } else {
        alert("Erro ao enviar musica: " + (data.error || "Erro desconhecido"));
      }
    } catch (err) {
      console.error(err);
      alert("Erro ao enviar musica");
    } finally {
      setUploading(false);
    }
  }

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  const TrackCard = ({ musica }) => {
    const isPlaying = tocandoId === musica.id;
    const isActive = musica.personalizada
      ? musicaAtualCustomUrl === musica.url
      : String(musicaAtualId) === String(musica.id);
    const isApplying = aplicandoId === musica.id;
    const isSuccess = successId === musica.id;
    const showTooltip = tooltipVisible[musica.id];

    return (
      <motion.div
        key={musica.id}
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        className={`music-track${isActive ? " music-track--active" : ""}${isPlaying ? " music-track--playing" : ""}`}
      >
        <button className={`music-play${isPlaying ? " music-play--playing" : ""}`} onClick={() => togglePlay(musica)} aria-label={isPlaying ? "Pausar" : "Tocar"}>
          <i className={`fa-solid ${isPlaying ? "fa-pause" : "fa-play"}`} />
        </button>
        <div className="music-track__info">
          <span className="music-track__name">{musica.titulo}</span>
          {isPlaying && <WaveformBars />}
          {isActive && !isPlaying && (
            <span className="music-track__badge"><i className="fa-solid fa-circle-check" /> Atual</span>
          )}
        </div>
        <button
          className={`music-select${isSuccess ? " music-select--success" : isActive ? " music-select--current" : ""}`}
          onClick={() => handleClickMusica(musica.id, musica.url, musica.personalizada)}
          disabled={isApplying}
          style={{ position: "relative" }}
        >
          {isApplying ? <span className="music-select__spinner" /> : isSuccess ? <><i className="fa-solid fa-check" /> Aplicada</> : isActive ? <><i className="fa-solid fa-check" /> Selecionada</> : "Selecionar"}
        </button>

        {musica.personalizada && musica.zero_creditos && (
          <div className="music-tooltip-wrap" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
            <i
              className="fa-solid fa-triangle-exclamation"
              style={{ color: "#f59e0b", fontSize: 14, cursor: "help", marginLeft: 6 }}
              onMouseEnter={() => setTooltipVisible((p) => ({ ...p, [musica.id]: true }))}
              onMouseLeave={() => setTooltipVisible((p) => ({ ...p, [musica.id]: false }))}
            />
            <AnimatePresence>
              {showTooltip && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  className="music-tooltip"
                  style={{
                    position: "absolute",
                    bottom: "calc(100% + 6px)",
                    right: 0,
                    background: "#1e1e2e",
                    color: "#fff",
                    padding: "8px 10px",
                    borderRadius: 8,
                    fontSize: 11,
                    width: 220,
                    zIndex: 10,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  }}
                >
                  Sua musica tem 24 horas de duracao no armazenamento do servidor. Compre seus creditos e evite a exclusao da track.
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="col2 prod-panel">
      <div className="prod-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="prod-header__icon-wrap"><i className="fa-solid fa-music" /></span>
          <h2 className="prod-header__title">Trilha sonora</h2>
        </div>
        <button className="prod-header__close" onClick={() => setVisivel("")} aria-label="Fechar"><i className="fa-solid fa-xmark" /></button>
      </div>

      <div className="prod-body" style={{ gap: 12 }}>
        {/* Tabs */}
        <div className="music-tabs" style={{ display: "flex", gap: 8, borderBottom: "2px solid #E8E1EF", paddingBottom: 0 }}>
          {["sistema", "minha-biblioteca"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: "8px 14px",
                borderRadius: "8px 8px 0 0",
                border: "none",
                borderBottom: tab === t ? "2px solid #73207C" : "2px solid transparent",
                background: "transparent",
                color: tab === t ? "#73207C" : "#5C4A63",
                fontWeight: tab === t ? 700 : 500,
                fontSize: 13,
                cursor: "pointer",
                marginBottom: -2,
              }}
            >
              {t === "sistema" ? "Sistema" : "Minha Biblioteca"}
            </button>
          ))}
        </div>

        {tab === "sistema" && (
          <>
            <p className="music-subtitle">Escolha a musica de fundo para o seu video</p>
            {loading && (
              <div className="music-skeletons">{[1, 2, 3, 4].map((i) => <div key={i} className="music-skeleton" />)}</div>
            )}
            {!loading && (
              <motion.div className="music-list" variants={containerVariants} initial="hidden" animate="visible">
                  {musicas.map((musica) => <TrackCard key={musica.id} musica={musica} />)}
                {/* CTA para Minha Biblioteca */}
                <div style={{ marginTop: 12, padding: "14px", background: "#F5EDFA", borderRadius: 12, textAlign: "center" }}>
                  <p style={{ fontSize: 13, color: "#3C005A", fontWeight: 600, marginBottom: 8 }}>Adicione sua propria musica</p>
                  <button className="prod-add-btn" onClick={() => setTab("minha-biblioteca")}>
                    <i className="fa-solid fa-cloud-arrow-up" /> Enviar musica
                  </button>
                </div>
              </motion.div>
            )}
            {!loading && musicas.length === 0 && (
              <div className="music-empty"><i className="fa-solid fa-music" /><p>Nenhuma musica disponivel</p></div>
            )}
          </>
        )}

        {tab === "minha-biblioteca" && (
          <>
            {minhasMusicas.length === 0 ? (
              <div
                className={`music-upload-zone${dragActive ? " music-upload-zone--active" : ""}`}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragActive ? "#73207C" : "#C8B8D4"}`,
                  borderRadius: 16,
                  padding: "40px 24px",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  background: dragActive ? "#F5EDFA" : "transparent",
                }}
              >
                {uploading ? (
                  <><span className="prod-spinner-sm" style={{ width: 32, height: 32 }} /><p style={{ marginTop: 12, fontSize: 14, color: "#5C4A63" }}>Enviando... {uploadProgress}%</p><div style={{ width: "80%", height: 6, background: "#e0d6e6", borderRadius: 3, marginTop: 8 }}><div style={{ width: `${uploadProgress}%`, height: "100%", background: "#73207C", borderRadius: 3, transition: "width 0.2s" }} /></div></>
                ) : (
                  <>
                    <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: 32, color: "#73207C" }} />
                    <p style={{ marginTop: 12, fontSize: 14, fontWeight: 600, color: "#0F0A15" }}>Adicione suas proprias musicas</p>
                    <p style={{ fontSize: 12, color: "#7a6b82", marginTop: 4 }}>MP3, WAV ou qualquer formato de audio</p>
                  </>
                )}
                <input type="file" ref={fileInputRef} style={{ display: "none" }} accept="audio/*" onChange={(e) => { if (e.target.files[0]) handleUpload(e.target.files[0]); e.target.value = ""; }} />
              </div>
            ) : (
              <>
                <motion.div className="music-list" variants={containerVariants} initial="hidden" animate="visible">
                    {minhasMusicas.map((musica) => <TrackCard key={musica.id} musica={musica} />)}
                </motion.div>
                <div style={{ marginTop: 12, padding: "14px", background: "#F5EDFA", borderRadius: 12, textAlign: "center" }}>
                  <button
                    className="prod-add-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <i className="fa-solid fa-cloud-arrow-up" /> Enviar nova musica
                  </button>
                  <input type="file" ref={fileInputRef} style={{ display: "none" }} accept="audio/*" onChange={(e) => { if (e.target.files[0]) handleUpload(e.target.files[0]); e.target.value = ""; }} />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Musicas;
